/**
 * Блок «Задачи» (тикет T18b, спецификация § 7).
 *
 * Цветную полосу срочности считает сервер по порогам фирмы — здесь её только показывают.
 * Считай её оболочка сама, два человека с разными часовыми поясами увидели бы у одной
 * Задачи разный цвет.
 *
 * Цвет никогда не остаётся единственным признаком (§ 14.5): рядом стоит слово. Тому, кто
 * не различает красное и зелёное, полоса не говорит ничего.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Api, Task } from '../net/api.js';
import { Notice } from '../ui/controls.js';
import { useSettings } from '../state/settings.js';
import type { PhraseKey } from '../i18n/dictionary.js';

const URGENCY_COLOR: Readonly<Record<string, string>> = {
  normal: 'bg-ok',
  warning: 'bg-warn',
  overdue: 'bg-danger',
};

const STATE_PHRASE: Readonly<Record<string, PhraseKey>> = {
  assigned: 'tasks.assigned',
  in_review: 'tasks.delivered',
  accepted: 'tasks.accepted',
};

export function Tasks({ api }: { readonly api: Api }): ReactNode {
  const { t, appearance } = useSettings();
  const [tasks, setTasks] = useState<readonly Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = useCallback(
    async (alive: () => boolean): Promise<void> => {
      setLoading(true);
      try {
        const got = await api.tasks();
        // Ответ, обогнанный следующим запросом, выбрасываем: пришедший позже старый
        // список лёг бы поверх нового, и человек увидел бы уже принятые Задачи.
        if (!alive()) return;
        setTasks(got);
        setRefusal(null);
      } catch (error) {
        if (!alive()) return;
        setRefusal(error instanceof Error ? error.message : t('tasks.failed'));
      } finally {
        if (alive()) setLoading(false);
      }
    },
    [api, t],
  );

  useEffect(() => {
    let current = true;
    void load(() => current);
    return () => {
      current = false;
    };
  }, [load]);

  const day = (moment: string): string => {
    const when = new Date(moment);
    if (Number.isNaN(when.getTime())) return '';
    return new Intl.DateTimeFormat(appearance.language, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: appearance.timeFormat === '12h' ? 'h12' : 'h23',
    }).format(when);
  };

  if (refusal) {
    return (
      <Notice kind="error" onClose={() => setRefusal(null)} closeLabel={t('common.close')}>
        {refusal}
      </Notice>
    );
  }

  if (loading) return <p className="text-ink-soft">{t('common.loading')}</p>;
  if (tasks.length === 0) return <p className="text-ink-soft">{t('tasks.empty')}</p>;

  return (
    <ul className="flex flex-col gap-2">
      {tasks.map((task) => {
        const overdue = task.urgency === 'overdue';
        return (
          <li key={task.id} className="flex items-stretch gap-2">
            <span
              aria-hidden="true"
              className={`w-1 shrink-0 rounded ${URGENCY_COLOR[task.urgency] ?? 'bg-line'}`}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate">{task.title}</p>
              <p className="text-xs text-ink-soft">
                {t(STATE_PHRASE[task.state] ?? 'tasks.assigned')} ·{' '}
                {overdue ? t('tasks.overdue') : t('tasks.due')}:{' '}
                <time dateTime={task.dueAt}>{day(task.dueAt)}</time>
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
